import { useCallback, useMemo } from 'react';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Permission, useCurrentCompany, useCurrentUser, usePermission } from '@bonusly/who-am-i';

/** Entry point types for spotlight generation */
export type SpotlightGenerationEntryPoint =
	| 'promo_banner'
	| 'action_buttons'
	| 'homepage_banner'
	| 'sidebar_team_menu'
	| 'team_hub_sidebar'
	| 'givebox_actions'
	| 'manager_recaps_page';

/** Parameters for the generation analytics hook */
export interface UseSpotlightGenerationAnalyticsParams {
	/** Where the user initiated spotlight generation */
	entryPoint: SpotlightGenerationEntryPoint;
	/** Target team member's ID (who the spotlight is for) */
	employeeId: string;
	/** Number of direct reports the current user has */
	numberOfDirectReports?: number;
}

/** Base properties included on all generation events */
interface GenerationBaseProperties {
	company_id: string;
	user_id: string;
	user_role: 'admin' | 'manager';
	number_of_direct_reports: number;
}

/** Properties for the Timeframe Adjusted event */
export interface TimeframeAdjustedProps {
	originalStart: string;
	originalEnd: string;
	finalStart: string;
	finalEnd: string;
	adjustmentCount: number;
}

/**
 * Analytics hook for Spotlight generation flow.
 *
 * Tracks events related to creating/generating spotlights from the Your Team page.
 * All events include base properties: company_id, user_id, user_role, number_of_direct_reports.
 *
 * Usage:
 * ```tsx
 * const { trackListOpened, trackGeneratorOpened, trackGenerationStarted, trackTimeframeAdjusted } =
 *   useSpotlightGenerationAnalytics({
 *     entryPoint: 'promo_banner',
 *     employeeId: targetUserId,
 *     numberOfDirectReports: directReports.length,
 *   });
 *
 * // Track when modal opens showing spotlight list
 * trackListOpened();
 *
 * // Track when user clicks "Create spotlight" button
 * trackGeneratorOpened();
 *
 * // Track when Generate button is clicked
 * trackGenerationStarted(preselectedTimeframe);
 *
 * // Track if timeframe was adjusted (call before trackGenerationStarted if dates changed)
 * trackTimeframeAdjusted({ originalStart, originalEnd, finalStart, finalEnd, adjustmentCount });
 * ```
 */
export const useSpotlightGenerationAnalytics = ({ entryPoint, employeeId, numberOfDirectReports = 0 }: UseSpotlightGenerationAnalyticsParams) => {
	const { track } = useAnalytics();
	const currentUser = useCurrentUser();
	const currentCompany = useCurrentCompany();
	const canViewAdmin = usePermission(Permission.CanViewAdmin);

	// Compute base properties once, memoized on dependency changes
	const baseProperties: GenerationBaseProperties = useMemo(
		() => ({
			company_id: currentCompany?.id ?? '',
			user_id: currentUser?.id ?? '',
			user_role: canViewAdmin ? 'admin' : 'manager',
			number_of_direct_reports: numberOfDirectReports,
		}),
		[currentCompany?.id, currentUser?.id, canViewAdmin, numberOfDirectReports]
	);

	/**
	 * Track when the spotlight list modal is opened.
	 * Fires when the modal opens showing existing spotlights.
	 */
	const trackListOpened = useCallback(() => {
		track(AnalyticsEvent.Spotlight.ListOpened, {
			...baseProperties,
			entry_point: entryPoint,
			employee_id: employeeId,
		});
	}, [track, baseProperties, entryPoint, employeeId]);

	/**
	 * Track when the user clicks "Create spotlight" to open the generator form.
	 * Enables funnel analysis: list opened → generator opened → generated.
	 */
	const trackGeneratorOpened = useCallback(() => {
		track(AnalyticsEvent.Spotlight.GeneratorOpened, {
			...baseProperties,
			entry_point: entryPoint,
			employee_id: employeeId,
		});
	}, [track, baseProperties, entryPoint, employeeId]);

	/**
	 * Track when the user clicks the Generate button.
	 * @param preselectedTimeframe - true if the user didn't change the default date range
	 */
	const trackGenerationStarted = useCallback(
		(preselectedTimeframe: boolean) => {
			track(AnalyticsEvent.Spotlight.GenerationStarted, {
				...baseProperties,
				entry_point: entryPoint,
				employee_id: employeeId,
				preselected_timeframe: preselectedTimeframe,
			});
		},
		[track, baseProperties, entryPoint, employeeId]
	);

	/**
	 * Track when the user adjusts the timeframe from the default.
	 * Should be called before trackGenerationStarted if dates were changed.
	 */
	const trackTimeframeAdjusted = useCallback(
		({ originalStart, originalEnd, finalStart, finalEnd, adjustmentCount }: TimeframeAdjustedProps) => {
			track(AnalyticsEvent.Spotlight.TimeframeAdjusted, {
				...baseProperties,
				entry_point: entryPoint,
				employee_id: employeeId,
				original_start: originalStart,
				original_end: originalEnd,
				final_start: finalStart,
				final_end: finalEnd,
				adjustment_count: adjustmentCount,
			});
		},
		[track, baseProperties, entryPoint, employeeId]
	);

	return {
		trackListOpened,
		trackGeneratorOpened,
		trackGenerationStarted,
		trackTimeframeAdjusted,
	};
};
